import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { trFold, matchesAll } from "@/lib/trFold";
import PageHeader from "@/components/PageHeader";
import MaterialDetailSheet from "@/components/MaterialDetailSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Loader2, AlertTriangle, Scissors, Boxes, Truck, ExternalLink, Search, X, ShieldAlert, Edit3, Sparkles, FileSpreadsheet, Package, ScanLine } from "lucide-react";
import { toast } from "sonner";
import DefinitionSelect from "@/components/DefinitionSelect";
import SearchPicker from "@/components/SearchPicker";
import AiStockDraftDialog from "@/components/AiStockDraftDialog";
import QuickSupplierDialog from "@/components/QuickSupplierDialog";
import BulkStockDialog from "@/components/BulkStockDialog";
import GroupedStockView from "@/components/GroupedStockView";
import { useAuth } from "@/context/AuthContext";

const fabricEmpty = { code: "", name: "", fabric_type: "", color: "", composition: "", weight_gsm: "", stock_kg: "", stock_metre: "", min_stock_kg: "", min_stock_unit: "kg", location: "", primary_supplier_id: "" };
const accEmpty = { code: "", name: "", category: "", unit: "adet", stock: "", min_stock: "", location: "", primary_supplier_id: "" };

const CATEGORIES = {
  reflektor: "Reflektif",
  iplik: "İplik",
  fermuar: "Fermuar",
  dugme: "Düğme / Çıtçıt",
  etiket: "Etiket",
  diger: "Diğer",
};

export default function Inventory() {
  const navigate = useNavigate();
  const [fabrics, setFabrics] = useState([]);
  const [bulkStockOpen, setBulkStockOpen] = useState(false);
  const [accessories, setAccessories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [fabricTypes, setFabricTypes] = useState([]);
  const [fabricNames, setFabricNames] = useState([]);
  const [compositions, setCompositions] = useState([]);
  const [colors, setColors] = useState([]);
  const [accessoryCats, setAccessoryCats] = useState([]);
  const [accessoryNames, setAccessoryNames] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  // Radix Select `value` prop unique olmalı; aynı isimli birden fazla depo
  // (test kalıntısı vb.) React duplicate-key uyarısına yol açar. İlk
  // occurrence'i tut, kalanları at.
  const uniqueWarehouses = useMemo(() => {
    const seen = new Set();
    return warehouses.filter((w) => {
      if (!w?.name || seen.has(w.name)) return false;
      seen.add(w.name);
      return true;
    });
  }, [warehouses]);
  const [loading, setLoading] = useState(true);
  const [fabOpen, setFabOpen] = useState(false);
  const [aiFabOpen, setAiFabOpen] = useState(false);
  const [aiAccOpen, setAiAccOpen] = useState(false);
  // FAZ 5 M5: hızlı tedarikçi ekleme — hem kumaş hem aksesuar formundan tetiklenir
  const [quickSupplierFor, setQuickSupplierFor] = useState(null); // "fabric" | "accessory" | null
  const quickSupplierForRef = useRef(null);
  const { can } = useAuth();
  const canAiFabric = can("fabrics", "ai_create") && can("fabrics", "add");
  const canAiAccessory = can("accessories", "ai_create") && can("accessories", "add");
  const [accOpen, setAccOpen] = useState(false);
  const [fabForm, setFabForm] = useState(fabricEmpty);
  const [accForm, setAccForm] = useState(accEmpty);
  const [editingFabricId, setEditingFabricId] = useState(null);
  const [editingAccessoryId, setEditingAccessoryId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fabErrors, setFabErrors] = useState([]);
  const [accErrors, setAccErrors] = useState([]);
  const [detail, setDetail] = useState({ open: false, type: null, id: null });
  // Search + filter state (shared per-tab)
  const [fabSearch, setFabSearch] = useState("");
  const [fabFilterType, setFabFilterType] = useState("");
  const [fabFilterSupplier, setFabFilterSupplier] = useState("");
  const [fabFilterStock, setFabFilterStock] = useState(""); // "critical" | "empty" | "ok" | ""
  const [accSearch, setAccSearch] = useState("");
  const [accFilterCat, setAccFilterCat] = useState("");
  const [accFilterSupplier, setAccFilterSupplier] = useState("");
  const [accFilterStock, setAccFilterStock] = useState("");
  // ÜRÜNLER sekmesi — canonical stocks (ürün × varyant × depo)
  const [prodTotal, setProdTotal] = useState(0);
  const [prodRefresh, setProdRefresh] = useState(0);

  const load = async () => {
    try {
      const [f, a, s, ft, ac, fn, cm, cl, an, w] = await Promise.all([
        api.get("/inventory/fabrics"),
        api.get("/inventory/accessories"),
        api.get("/suppliers"),
        api.get("/definitions/fabric_type").catch(() => ({ data: [] })),
        api.get("/definitions/accessory_category").catch(() => ({ data: [] })),
        api.get("/definitions/fabric_name").catch(() => ({ data: [] })),
        api.get("/definitions/composition").catch(() => ({ data: [] })),
        api.get("/definitions/color").catch(() => ({ data: [] })),
        api.get("/definitions/accessory_name").catch(() => ({ data: [] })),
        api.get("/warehouses").catch(() => ({ data: [] })),
      ]);
      setFabrics(f.data);
      setAccessories(a.data);
      setSuppliers(s.data);
      setFabricTypes((ft.data || []).filter((x) => x.is_active !== false));
      setAccessoryCats((ac.data || []).filter((x) => x.is_active !== false));
      setFabricNames((fn.data || []).filter((x) => x.is_active !== false));
      setCompositions((cm.data || []).filter((x) => x.is_active !== false));
      setColors((cl.data || []).filter((x) => x.is_active !== false));
      setAccessoryNames((an.data || []).filter((x) => x.is_active !== false));
      setWarehouses((w.data || []).filter((x) => x.is_active !== false));
      setProdRefresh((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);


  // Diğer bileşenlerden (AI Ürün Oluşturucu → Yeni Stok Kartı vb.) tetiklenen
  // otomatik refresh event'lerini dinle.
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("fucan:fabrics-refresh", handler);
    window.addEventListener("fucan:accessories-refresh", handler);
    return () => {
      window.removeEventListener("fucan:fabrics-refresh", handler);
      window.removeEventListener("fucan:accessories-refresh", handler);
    };
  }, []);

  const submitFabric = async (e) => {
    e.preventDefault();
    // Enforce required selections
    const missing = [];
    if (!fabForm.name?.trim()) missing.push("Kumaş Adı");
    if (!fabForm.fabric_type?.trim()) missing.push("Kumaş Türü");
    if (!fabForm.composition?.trim()) missing.push("Kompozisyon");
    if (!fabForm.primary_supplier_id?.trim()) missing.push("Tedarikçi");
    if (missing.length) {
      setFabErrors(missing);
      toast.error(`Zorunlu alan(lar): ${missing.join(", ")}`);
      return;
    }
    setFabErrors([]);
    setSaving(true);
    try {
      const payload = {
        ...fabForm,
        weight_gsm: Number(fabForm.weight_gsm) || 0,
        stock_kg: Number(fabForm.stock_kg) || 0,
        stock_metre: Number(fabForm.stock_metre) || 0,
        min_stock_kg: Number(fabForm.min_stock_kg) || 0,
        min_stock_unit: fabForm.min_stock_unit || "kg",
      };
      if (editingFabricId) {
        await api.patch(`/inventory/fabrics/${editingFabricId}`, payload);
        toast.success("Kumaş güncellendi");
      } else {
        await api.post("/inventory/fabrics", payload);
        toast.success("Kumaş eklendi");
      }
      setFabOpen(false);
      setFabForm(fabricEmpty);
      setEditingFabricId(null);
      load();
      // Diğer bileşenler (AI Ürün Oluşturucu vb.) haberdar olsun
      window.dispatchEvent(new CustomEvent("fucan:fabrics-refresh"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openEditFabric = (f) => {
    setEditingFabricId(f.id);
    setFabForm({
      code: f.code || "",
      name: f.name || "",
      fabric_type: f.fabric_type || "",
      color: f.color || "",
      composition: f.composition || "",
      weight_gsm: f.weight_gsm ?? "",
      stock_kg: f.stock_kg ?? "",
      stock_metre: f.stock_metre ?? "",
      min_stock_kg: f.min_stock_kg ?? "",
      min_stock_unit: f.min_stock_unit || "kg",
      location: f.location || "",
      primary_supplier_id: f.primary_supplier_id || "",
    });
    setFabOpen(true);
  };

  const openNewFabric = () => {
    setEditingFabricId(null);
    setFabForm(fabricEmpty);
    setFabOpen(true);
  };

  const submitAcc = async (e) => {
    e.preventDefault();
    // Enforce required selections
    const missing = [];
    if (!accForm.name?.trim()) missing.push("Aksesuar Adı");
    if (!accForm.category?.trim()) missing.push("Kategori");
    if (!accForm.primary_supplier_id?.trim()) missing.push("Tedarikçi");
    if (missing.length) {
      setAccErrors(missing);
      toast.error(`Zorunlu alan(lar): ${missing.join(", ")}`);
      return;
    }
    setAccErrors([]);
    setSaving(true);
    try {
      const payload = {
        ...accForm,
        stock: Number(accForm.stock) || 0,
        min_stock: Number(accForm.min_stock) || 0,
      };
      if (editingAccessoryId) {
        await api.patch(`/inventory/accessories/${editingAccessoryId}`, payload);
        toast.success("Aksesuar güncellendi");
      } else {
        await api.post("/inventory/accessories", payload);
        toast.success("Aksesuar eklendi");
      }
      setAccOpen(false);
      setAccForm(accEmpty);
      setEditingAccessoryId(null);
      load();
      window.dispatchEvent(new CustomEvent("fucan:accessories-refresh"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openEditAccessory = (a) => {
    setEditingAccessoryId(a.id);
    setAccForm({
      code: a.code || "",
      name: a.name || "",
      category: a.category || "",
      unit: a.unit || "adet",
      stock: a.stock ?? "",
      min_stock: a.min_stock ?? "",
      location: a.location || "",
      primary_supplier_id: a.primary_supplier_id || "",
    });
    setAccOpen(true);
  };

  const openNewAccessory = () => {
    setEditingAccessoryId(null);
    setAccForm(accEmpty);
    setAccOpen(true);
  };

  const removeFabric = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/inventory/fabrics/${id}`);
      setFabrics((prev) => prev.filter((f) => f.id !== id));
      toast.success("Kumaş silindi");
      window.dispatchEvent(new CustomEvent("fucan:fabrics-refresh"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeAcc = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/inventory/accessories/${id}`);
      setAccessories((prev) => prev.filter((a) => a.id !== id));
      toast.success("Aksesuar silindi");
      window.dispatchEvent(new CustomEvent("fucan:accessories-refresh"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const supplierById = useMemo(() => {
    const m = {};
    suppliers.forEach((s) => { m[s.id] = s; });
    return m;
  }, [suppliers]);

  const filteredFabrics = useMemo(() => {
    const term = trFold(fabSearch.trim());
    return fabrics.filter((f) => {
      if (term) {
        const sup = supplierById[f.primary_supplier_id] || {};
        const supName = (sup.name || f.primary_supplier_name || "");
        const brands = (sup.brand_names || []).join(" ");
        const cats = (sup.category_names || []).join(" ");
        const tags = (f.tags || []).join(" ");
        if (!matchesAll(term, [f.code, f.name, f.color, f.composition, f.fabric_type, supName, brands, cats, tags])) return false;
      }
      if (fabFilterType && f.fabric_type !== fabFilterType) return false;
      if (fabFilterSupplier && f.primary_supplier_id !== fabFilterSupplier) return false;
      const unit = (f.min_stock_unit || "kg") === "metre" ? "metre" : "kg";
      const stock = Number((unit === "metre" ? f.stock_metre : f.stock_kg) || 0);
      const min = Number(f.min_stock_kg || 0);
      if (fabFilterStock === "empty" && stock > 0) return false;
      if (fabFilterStock === "critical" && !(stock > 0 && stock <= min && min > 0)) return false;
      if (fabFilterStock === "ok" && !(stock > min || min === 0)) return false;
      return true;
    });
  }, [fabrics, fabSearch, fabFilterType, fabFilterSupplier, fabFilterStock, supplierById]);

  const filteredAccessories = useMemo(() => {
    const term = trFold(accSearch.trim());
    return accessories.filter((a) => {
      if (term) {
        const sup = supplierById[a.primary_supplier_id] || {};
        const supName = (sup.name || a.primary_supplier_name || "");
        const brands = (sup.brand_names || []).join(" ");
        const cats = (sup.category_names || []).join(" ");
        const tags = (a.tags || []).join(" ");
        if (!matchesAll(term, [a.code, a.name, a.category, supName, brands, cats, tags])) return false;
      }
      if (accFilterCat && a.category !== accFilterCat) return false;
      if (accFilterSupplier && a.primary_supplier_id !== accFilterSupplier) return false;
      const stock = Number(a.stock || 0);
      const min = Number(a.min_stock || 0);
      if (accFilterStock === "empty" && stock > 0) return false;
      if (accFilterStock === "critical" && !(stock > 0 && stock <= min && min > 0)) return false;
      if (accFilterStock === "ok" && !(stock > min || min === 0)) return false;
      return true;
    });
  }, [accessories, accSearch, accFilterCat, accFilterSupplier, accFilterStock, supplierById]);

  const criticalCount = useMemo(() => {
    const fabCritical = fabrics.filter((f) => {
      const unit = (f.min_stock_unit || "kg") === "metre" ? "metre" : "kg";
      const stock = Number((unit === "metre" ? f.stock_metre : f.stock_kg) || 0);
      const min = Number(f.min_stock_kg || 0);
      return min > 0 && stock <= min;
    }).length;
    const accCritical = accessories.filter((a) => Number(a.stock || 0) <= Number(a.min_stock || 0) && Number(a.min_stock || 0) > 0).length;
    const fabEmpty = fabrics.filter((f) => Number(f.stock_kg || 0) <= 0).length;
    const accEmpty = accessories.filter((a) => Number(a.stock || 0) <= 0).length;
    return { fabric: fabCritical, accessory: accCritical, empty: fabEmpty + accEmpty, total: fabCritical + accCritical };
  }, [fabrics, accessories]);

  const clearFabFilters = () => { setFabSearch(""); setFabFilterType(""); setFabFilterSupplier(""); setFabFilterStock(""); };
  const clearAccFilters = () => { setAccSearch(""); setAccFilterCat(""); setAccFilterSupplier(""); setAccFilterStock(""); };

  return (
    <div>
      <PageHeader
        title="Stok Yönetimi"
        subtitle="Kumaş çift-metrikli (kg & metre) · Aksesuar birim bazlı"
        breadcrumbs={["Modüller", "Stok"]}
        actions={
          <Button onClick={() => navigate("/el-terminali")} className="rounded-sm bg-blue-600 hover:bg-blue-500 text-xs uppercase tracking-wider" data-testid="open-hand-terminal-btn">
            <ScanLine className="w-4 h-4 mr-1.5" /> El Terminali
          </Button>
        }
      />

      <div className="p-6 lg:p-8">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* Kritik Stok Uyarı Barı */}
            {(criticalCount.total > 0 || criticalCount.empty > 0) && (
              <div
                data-testid="critical-stock-banner"
                className="mb-4 rounded-sm border border-rose-900/60 bg-gradient-to-r from-rose-950/40 via-amber-950/30 to-transparent px-4 py-3 flex items-center gap-4 flex-wrap"
              >
                <div className="flex items-center gap-2 text-rose-300">
                  <ShieldAlert className="w-5 h-5" />
                  <span className="text-sm font-mono uppercase tracking-widest font-semibold">Kritik Stok Uyarısı</span>
                </div>
                <div className="flex items-center gap-4 text-xs font-mono flex-wrap">
                  {criticalCount.fabric > 0 && (
                    <span className="text-amber-300"><b className="text-lg font-bold text-amber-200">{criticalCount.fabric}</b> kumaş kritik seviyede</span>
                  )}
                  {criticalCount.accessory > 0 && (
                    <span className="text-amber-300"><b className="text-lg font-bold text-amber-200">{criticalCount.accessory}</b> aksesuar kritik seviyede</span>
                  )}
                  {criticalCount.empty > 0 && (
                    <span className="text-rose-300"><b className="text-lg font-bold text-rose-200">{criticalCount.empty}</b> malzeme tükenmiş</span>
                  )}
                </div>
              </div>
            )}
            <Tabs defaultValue="products" className="w-full">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <TabsList className="bg-slate-900 border border-slate-800 rounded-sm h-auto p-1 flex-wrap">
                <TabsTrigger
                  value="products"
                  data-testid="tab-products"
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2"
                >
                  <Package className="w-3.5 h-3.5 mr-2" /> Ürünler ({prodTotal})
                </TabsTrigger>
                <TabsTrigger
                  value="fabric"
                  data-testid="tab-fabric"
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2"
                >
                  <Scissors className="w-3.5 h-3.5 mr-2" /> Kumaş ({fabrics.length})
                </TabsTrigger>
                <TabsTrigger
                  value="accessory"
                  data-testid="tab-accessory"
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2"
                >
                  <Boxes className="w-3.5 h-3.5 mr-2" /> Aksesuar ({accessories.length})
                </TabsTrigger>
              </TabsList>
              <Button
                data-testid="bulk-stock-open-btn"
                size="sm"
                onClick={() => setBulkStockOpen(true)}
                className="bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-sm text-xs uppercase tracking-wider border border-slate-700"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 mr-2" /> Toplu Stok Yönetimi
              </Button>
            </div>

            <TabsContent value="fabric" className="mt-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="relative flex-1 min-w-[240px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
                  <Input
                    data-testid="fabric-search-input"
                    placeholder="Kod, isim, renk veya kompozisyonla ara..."
                    value={fabSearch}
                    onChange={(e) => setFabSearch(e.target.value)}
                    className="pl-9 bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 text-xs h-9"
                  />
                </div>
                <Select value={fabFilterType || "__all"} onValueChange={(v) => setFabFilterType(v === "__all" ? "" : v)}>
                  <SelectTrigger data-testid="fabric-filter-type" className="w-[160px] bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
                    <SelectValue placeholder="Tüm kumaş türleri" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="__all">Tüm kumaş türleri</SelectItem>
                    {fabricTypes.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={fabFilterSupplier || "__all"} onValueChange={(v) => setFabFilterSupplier(v === "__all" ? "" : v)}>
                  <SelectTrigger data-testid="fabric-filter-supplier" className="w-[170px] bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
                    <SelectValue placeholder="Tüm tedarikçiler" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-72">
                    <SelectItem value="__all">Tüm tedarikçiler</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={fabFilterStock || "__all"} onValueChange={(v) => setFabFilterStock(v === "__all" ? "" : v)}>
                  <SelectTrigger data-testid="fabric-filter-stock" className="w-[150px] bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
                    <SelectValue placeholder="Tüm stok durumu" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="__all">Tüm stok durumu</SelectItem>
                    <SelectItem value="ok">Yeterli</SelectItem>
                    <SelectItem value="critical">Kritik seviyede</SelectItem>
                    <SelectItem value="empty">Tükenmiş</SelectItem>
                  </SelectContent>
                </Select>
                {(fabSearch || fabFilterType || fabFilterSupplier || fabFilterStock) && (
                  <Button variant="ghost" size="sm" onClick={clearFabFilters} data-testid="fabric-clear-filters" className="text-slate-400 hover:text-slate-100 rounded-sm h-9">
                    <X className="w-3.5 h-3.5 mr-1" /> Temizle
                  </Button>
                )}
                <div className="text-[10px] font-mono uppercase text-slate-500 ml-auto">
                  {filteredFabrics.length} / {fabrics.length}
                </div>
                {canAiFabric && (
                  <Button
                    data-testid="ai-fabric-open-btn"
                    onClick={() => setAiFabOpen(true)}
                    className="bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors h-9"
                  >
                    <Sparkles className="w-4 h-4 mr-2" /> AI ile Kumaş
                  </Button>
                )}
                <Button
                  data-testid="add-fabric-btn"
                  onClick={openNewFabric}
                  className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors h-9"
                >
                  <Plus className="w-4 h-4 mr-2" /> Kumaş Ekle
                </Button>
              </div>
              <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-slate-800">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kod</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kumaş</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Renk</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">GSM</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Stok (Kg)</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Stok (Metre)</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Kritik (Kg)</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Konum</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFabrics.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-10 text-slate-500 text-sm">
                          {fabrics.length === 0 ? "Kumaş bulunmuyor." : "Filtreye uyan kumaş yok."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredFabrics.map((f) => {
                        const unit = (f.min_stock_unit || "kg") === "metre" ? "metre" : "kg";
                        const minValue = Number(f.min_stock_kg || 0);
                        const stockValue = Number((unit === "metre" ? f.stock_metre : f.stock_kg) || 0);
                        const low = minValue > 0 && stockValue < minValue;
                        return (
                          <TableRow key={f.id} className="border-slate-800 hover:bg-slate-800/40 cursor-pointer" data-testid={`fabric-row-${f.id}`} onClick={() => navigate(`/stok/kumas/${f.id}`)}>
                            <TableCell className="font-mono text-xs text-blue-500 font-semibold hover:underline">{f.code}</TableCell>
                            <TableCell className="text-slate-100 font-medium hover:underline">
                              <div className="flex items-center gap-2">
                                {low && <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />}
                                {f.name}
                              </div>
                              {f.composition && <div className="text-[10px] text-slate-500 font-mono mt-0.5">{f.composition}</div>}
                            </TableCell>
                            <TableCell>
                              {f.primary_supplier_name ? (
                                <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 border border-blue-900/60 text-blue-300 bg-blue-950/30 rounded-sm font-mono">
                                  <Truck className="w-2.5 h-2.5" /> {f.primary_supplier_name}
                                </span>
                              ) : (
                                <span className="text-slate-600 text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-slate-300 text-sm">{f.color || "-"}</TableCell>
                            <TableCell className="text-right font-mono text-slate-300 text-xs tabular-nums">{f.weight_gsm || "-"}</TableCell>
                            <TableCell className={`text-right font-mono font-semibold tabular-nums ${low ? "text-yellow-400" : "text-slate-100"}`}>
                              {f.stock_kg.toFixed(1)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-slate-300 tabular-nums">{f.stock_metre.toFixed(1)}</TableCell>
                            <TableCell className="text-right font-mono text-slate-500 text-xs tabular-nums">
                              {f.min_stock_kg > 0
                                ? `${f.min_stock_kg.toFixed(1)} ${f.min_stock_unit || "kg"}`
                                : "-"}
                            </TableCell>
                            <TableCell className="text-slate-500 text-xs font-mono">{f.location || "-"}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  data-testid={`edit-fabric-${f.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); openEditFabric(f); }}
                                  className="text-slate-500 hover:text-emerald-400 hover:bg-emerald-950/30 rounded-sm"
                                  title="Düzenle"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </Button>
                                <Button
                                  data-testid={`trace-fabric-${f.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/stok/kumas/${f.id}`); }}
                                  className="text-slate-500 hover:text-blue-400 hover:bg-blue-950/30 rounded-sm"
                                  title="Detay & Lot Takibi"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                                <Button
                                  data-testid={`delete-fabric-${f.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); removeFabric(f.id); }}
                                  className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm"
                                  title="Sil"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            <TabsContent value="accessory" className="mt-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="relative flex-1 min-w-[240px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
                  <Input
                    data-testid="accessory-search-input"
                    placeholder="Kod, isim veya kategori ile ara..."
                    value={accSearch}
                    onChange={(e) => setAccSearch(e.target.value)}
                    className="pl-9 bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 text-xs h-9"
                  />
                </div>
                <Select value={accFilterCat || "__all"} onValueChange={(v) => setAccFilterCat(v === "__all" ? "" : v)}>
                  <SelectTrigger data-testid="accessory-filter-cat" className="w-[160px] bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
                    <SelectValue placeholder="Tüm kategoriler" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="__all">Tüm kategoriler</SelectItem>
                    {accessoryCats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={accFilterSupplier || "__all"} onValueChange={(v) => setAccFilterSupplier(v === "__all" ? "" : v)}>
                  <SelectTrigger data-testid="accessory-filter-supplier" className="w-[170px] bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
                    <SelectValue placeholder="Tüm tedarikçiler" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-72">
                    <SelectItem value="__all">Tüm tedarikçiler</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={accFilterStock || "__all"} onValueChange={(v) => setAccFilterStock(v === "__all" ? "" : v)}>
                  <SelectTrigger data-testid="accessory-filter-stock" className="w-[150px] bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
                    <SelectValue placeholder="Tüm stok durumu" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="__all">Tüm stok durumu</SelectItem>
                    <SelectItem value="ok">Yeterli</SelectItem>
                    <SelectItem value="critical">Kritik seviyede</SelectItem>
                    <SelectItem value="empty">Tükenmiş</SelectItem>
                  </SelectContent>
                </Select>
                {(accSearch || accFilterCat || accFilterSupplier || accFilterStock) && (
                  <Button variant="ghost" size="sm" onClick={clearAccFilters} data-testid="accessory-clear-filters" className="text-slate-400 hover:text-slate-100 rounded-sm h-9">
                    <X className="w-3.5 h-3.5 mr-1" /> Temizle
                  </Button>
                )}
                <div className="text-[10px] font-mono uppercase text-slate-500 ml-auto">
                  {filteredAccessories.length} / {accessories.length}
                </div>
                {canAiAccessory && (
                  <Button
                    data-testid="ai-accessory-open-btn"
                    onClick={() => setAiAccOpen(true)}
                    className="bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors h-9"
                  >
                    <Sparkles className="w-4 h-4 mr-2" /> AI ile Aksesuar
                  </Button>
                )}
                <Button
                  data-testid="add-accessory-btn"
                  onClick={openNewAccessory}
                  className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors h-9"
                >
                  <Plus className="w-4 h-4 mr-2" /> Aksesuar Ekle
                </Button>
              </div>
              <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-slate-800">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kod</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Aksesuar</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kategori</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Stok</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birim</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Kritik</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Konum</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAccessories.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-10 text-slate-500 text-sm">
                          {accessories.length === 0 ? "Aksesuar bulunmuyor." : "Filtreye uyan aksesuar yok."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAccessories.map((a) => {
                        const low = a.stock < a.min_stock;
                        return (
                          <TableRow key={a.id} className="border-slate-800 hover:bg-slate-800/40 cursor-pointer" data-testid={`accessory-row-${a.id}`} onClick={() => navigate(`/stok/aksesuar/${a.id}`)}>
                            <TableCell className="font-mono text-xs text-blue-500 font-semibold hover:underline">{a.code}</TableCell>
                            <TableCell className="text-slate-100 font-medium hover:underline">
                              <div className="flex items-center gap-2">
                                {low && <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />}
                                {a.name}
                              </div>
                            </TableCell>
                            <TableCell>
                              {a.primary_supplier_name ? (
                                <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 border border-blue-900/60 text-blue-300 bg-blue-950/30 rounded-sm font-mono">
                                  <Truck className="w-2.5 h-2.5" /> {a.primary_supplier_name}
                                </span>
                              ) : (
                                <span className="text-slate-600 text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-slate-300 text-xs">{a.category || "—"}</TableCell>
                            <TableCell className={`text-right font-mono font-semibold tabular-nums ${low ? "text-yellow-400" : "text-slate-100"}`}>
                              {a.stock.toFixed(0)}
                            </TableCell>
                            <TableCell className="text-slate-400 text-xs font-mono">{a.unit}</TableCell>
                            <TableCell className="text-right font-mono text-slate-500 text-xs tabular-nums">{a.min_stock.toFixed(0)}</TableCell>
                            <TableCell className="text-slate-500 text-xs font-mono">{a.location || "-"}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  data-testid={`edit-accessory-${a.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); openEditAccessory(a); }}
                                  className="text-slate-500 hover:text-emerald-400 hover:bg-emerald-950/30 rounded-sm"
                                  title="Düzenle"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </Button>
                                <Button
                                  data-testid={`trace-accessory-${a.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/stok/aksesuar/${a.id}`); }}
                                  className="text-slate-500 hover:text-blue-400 hover:bg-blue-950/30 rounded-sm"
                                  title="Detay & Lot Takibi"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                                <Button
                                  data-testid={`delete-accessory-${a.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); removeAcc(a.id); }}
                                  className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm"
                                  title="Sil"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            <TabsContent value="products" className="mt-0">
              <GroupedStockView allWarehouses={warehouses} onTotal={setProdTotal} refreshKey={prodRefresh} />
            </TabsContent>
          </Tabs>
          </>
        )}
      </div>

      {/* Fabric dialog */}
      <Dialog open={fabOpen} onOpenChange={(v) => { setFabOpen(v); if (!v) { setEditingFabricId(null); setFabForm(fabricEmpty); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight">
              {editingFabricId ? "Kumaş Düzenle" : "Yeni Kumaş"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitFabric} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kod</Label>
                <Input
                  data-testid="fabric-code-input"
                  value={fabForm.code}
                  onChange={(e) => setFabForm((prev) => ({ ...prev, code: e.target.value }))}
                  placeholder="Boş bırakılırsa otomatik (KMS-###)"
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
                />
              </div>
              <DefinitionSelect
                label="Kumaş Türü"
                required
                value={fabForm.fabric_type}
                onChange={(v) => setFabForm((prev) => ({ ...prev, fabric_type: v }))}
                items={fabricTypes}
                kind="fabric_type"
                testId="fabric-type"
                placeholder="Kumaş türü seçin"
                addLabel="Yeni Kumaş Türü"
                onCreated={(created) => setFabricTypes((prev) => [...prev, created])}
              />
            </div>
            <DefinitionSelect
              label="Kumaş Adı"
              required
              value={fabForm.name}
              onChange={(v) => setFabForm((prev) => ({ ...prev, name: v }))}
              items={fabricNames}
              kind="fabric_name"
              testId="fabric-name"
              placeholder="Kumaş adı seçin"
              addLabel="Yeni Kumaş Adı Ekle"
              onCreated={(created) => setFabricNames((prev) => [...prev, created])}
            />
            <div className="grid grid-cols-2 gap-3">
              <DefinitionSelect
                label="Renk"
                value={fabForm.color}
                onChange={(v) => setFabForm((prev) => ({ ...prev, color: v }))}
                items={colors}
                kind="color"
                testId="fabric-color"
                placeholder="Renk seçin"
                addLabel="Yeni Renk Ekle"
                onCreated={(created) => setColors((prev) => [...prev, created])}
              />
              <DefinitionSelect
                label="Kompozisyon"
                required
                value={fabForm.composition}
                onChange={(v) => setFabForm((prev) => ({ ...prev, composition: v }))}
                items={compositions}
                kind="composition"
                testId="fabric-composition"
                placeholder="Kompozisyon seçin"
                addLabel="Yeni Kompozisyon"
                onCreated={(created) => setCompositions((prev) => [...prev, created])}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">GSM</Label>
                <Input type="number" value={fabForm.weight_gsm} onChange={(e) => setFabForm((prev) => ({ ...prev, weight_gsm: e.target.value }))} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Stok Kg</Label>
                <Input type="number" step="0.1" value={fabForm.stock_kg} onChange={(e) => setFabForm((prev) => ({ ...prev, stock_kg: e.target.value }))} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Stok Metre</Label>
                <Input type="number" step="0.1" value={fabForm.stock_metre} onChange={(e) => setFabForm((prev) => ({ ...prev, stock_metre: e.target.value }))} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kritik Stok Eşiği</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="fabric-min-stock-input"
                  type="number" step="0.01" min="0"
                  value={fabForm.min_stock_kg}
                  onChange={(e) => setFabForm((prev) => ({ ...prev, min_stock_kg: e.target.value }))}
                  placeholder="Örn: 100"
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono flex-1"
                />
                <Select
                  value={fabForm.min_stock_unit || "kg"}
                  onValueChange={(v) => setFabForm((prev) => ({ ...prev, min_stock_unit: v }))}
                >
                  <SelectTrigger
                    data-testid="fabric-min-stock-unit"
                    className="bg-slate-950 border-slate-700 rounded-sm w-[110px] font-mono"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="metre">metre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[10px] text-slate-500 font-mono">
                Bu eşiğin altına düşen stoklar "Kritik Stok Uyarısı" olarak listelenir.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                Birincil Tedarikçi<span className="text-rose-400 ml-1">*</span>
              </Label>
              <div className="flex gap-2 items-start">
                <div className="flex-1" data-testid="fabric-supplier-select">
                  <SearchPicker
                    items={suppliers.filter((s) => s.category === "kumas" || s.category === "genel")}
                    value={fabForm.primary_supplier_id}
                    onChange={(v) => setFabForm((prev) => ({ ...prev, primary_supplier_id: v || "" }))}
                    getId={(s) => s.id}
                    getLabel={(s) => s.name || ""}
                    getSubtitle={(s) => [s.tax_no, s.phone].filter(Boolean).join(" · ")}
                    filter={(s, q) => {
                      const f = (x) => (x || "").toLocaleLowerCase("tr-TR");
                      const t = f(q);
                      return f(s.name).includes(t) || f(s.tax_no).includes(t) || f(s.phone).includes(t);
                    }}
                    placeholder="Firma ünvanı / vergi no / telefon yazın…"
                    minChars={1}
                    testId="fabric-supplier-picker"
                  />
                  {!fabForm.primary_supplier_id && (
                    <div className="text-[9px] text-rose-400/80 mt-0.5">Tedarikçi zorunlu</div>
                  )}
                </div>
                <Button
                  type="button"
                  data-testid="fabric-new-supplier-btn"
                  onClick={() => { quickSupplierForRef.current = "fabric"; setQuickSupplierFor("fabric"); }}
                  variant="outline"
                  className="border-blue-700 text-blue-400 hover:bg-blue-950/40 rounded-sm shrink-0 px-3"
                  title="Yeni Tedarikçi Ekle"
                >
                  <Plus className="w-4 h-4 mr-1" /> Yeni
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Konum (Depo)</Label>
              <Select
                value={fabForm.location || "__none"}
                onValueChange={(v) => setFabForm((prev) => ({ ...prev, location: v === "__none" ? "" : v }))}
              >
                <SelectTrigger
                  data-testid="fabric-location-select"
                  className="bg-slate-950 border-slate-700 rounded-sm font-mono"
                >
                  <SelectValue placeholder="Depo seçin" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-60">
                  <SelectItem value="__none">— Konum seçilmedi —</SelectItem>
                  {uniqueWarehouses.map((w) => (
                    <SelectItem key={w.id} value={w.name}>
                      <span className="font-mono text-blue-400">{w.code}</span>
                      <span className="ml-2">{w.name}</span>
                    </SelectItem>
                  ))}
                  {uniqueWarehouses.length === 0 && (
                    <div className="px-2 py-3 text-center text-xs text-slate-500">
                      Depo tanımlı değil. Ayarlar → Depolar'dan ekleyin.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              {fabErrors.length > 0 && (
                <div
                  data-testid="fabric-validation-error"
                  role="alert"
                  className="mr-auto flex items-center gap-2 rounded-sm border border-rose-800 bg-rose-950/60 px-3 py-2 text-xs text-rose-200"
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 text-rose-400" />
                  <span>Zorunlu alan(lar): {fabErrors.join(", ")}</span>
                </div>
              )}
              <Button type="button" variant="outline" onClick={() => setFabOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
                İptal
              </Button>
              <Button data-testid="save-fabric-btn" type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingFabricId ? "Güncelle" : "Kaydet")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Accessory dialog */}
      <Dialog open={accOpen} onOpenChange={(v) => { setAccOpen(v); if (!v) { setEditingAccessoryId(null); setAccForm(accEmpty); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight">
              {editingAccessoryId ? "Aksesuar Düzenle" : "Yeni Aksesuar"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAcc} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kod</Label>
                <Input
                  data-testid="accessory-code-input"
                  value={accForm.code}
                  onChange={(e) => setAccForm((prev) => ({ ...prev, code: e.target.value }))}
                  placeholder="Boş bırakılırsa otomatik (AKS-###)"
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
                />
              </div>
              <DefinitionSelect
                label="Kategori"
                required
                value={accForm.category}
                onChange={(v) => setAccForm((prev) => ({ ...prev, category: v }))}
                items={accessoryCats}
                kind="accessory_category"
                testId="accessory-category"
                placeholder="Kategori seçin"
                addLabel="Yeni Kategori"
                onCreated={(created) => setAccessoryCats((prev) => [...prev, created])}
              />
            </div>
            <DefinitionSelect
              label="Aksesuar Adı"
              required
              value={accForm.name}
              onChange={(v) => setAccForm((prev) => ({ ...prev, name: v }))}
              items={accessoryNames}
              kind="accessory_name"
              testId="accessory-name"
              placeholder="Aksesuar adı seçin"
              addLabel="Yeni Aksesuar Adı Ekle"
              onCreated={(created) => setAccessoryNames((prev) => [...prev, created])}
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Stok</Label>
                <Input type="number" step="0.01" value={accForm.stock} onChange={(e) => setAccForm((prev) => ({ ...prev, stock: e.target.value }))} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Birim</Label>
                <Select value={accForm.unit || "adet"} onValueChange={(v) => setAccForm((prev) => ({ ...prev, unit: v }))}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="adet">adet</SelectItem>
                    <SelectItem value="metre">metre</SelectItem>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="kon">kon</SelectItem>
                    <SelectItem value="paket">paket</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kritik Stok Eşiği</Label>
              <Input
                data-testid="accessory-min-stock-input"
                type="number" step="0.01" min="0"
                value={accForm.min_stock}
                onChange={(e) => setAccForm((prev) => ({ ...prev, min_stock: e.target.value }))}
                placeholder="Örn: 50"
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
              />
              <p className="text-[10px] text-slate-500 font-mono">
                Bu eşiğin altına düşen stoklar "Kritik Stok Uyarısı" olarak listelenir.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                Birincil Tedarikçi<span className="text-rose-400 ml-1">*</span>
              </Label>
              <div className="flex gap-2">
                <Select value={accForm.primary_supplier_id || "none"} onValueChange={(v) => { if (!v) return; setAccForm((prev) => ({ ...prev, primary_supplier_id: v === "none" ? "" : v })); }}>
                  <SelectTrigger
                    data-testid="accessory-supplier-select"
                    className={`bg-slate-950 border-slate-700 rounded-sm flex-1 ${!accForm.primary_supplier_id ? "border-rose-700" : ""}`}
                  >
                    <SelectValue placeholder="Tedarikçi seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-60">
                    <SelectItem value="none">— Seçilmedi —</SelectItem>
                    {suppliers.filter((s) => s.category === "aksesuar" || s.category === "genel").map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  data-testid="accessory-new-supplier-btn"
                  onClick={() => { quickSupplierForRef.current = "accessory"; setQuickSupplierFor("accessory"); }}
                  variant="outline"
                  className="border-blue-700 text-blue-400 hover:bg-blue-950/40 rounded-sm shrink-0 px-3"
                  title="Yeni Tedarikçi Ekle"
                >
                  <Plus className="w-4 h-4 mr-1" /> Yeni
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Konum (Depo)</Label>
              <Select
                value={accForm.location || "__none"}
                onValueChange={(v) => setAccForm((prev) => ({ ...prev, location: v === "__none" ? "" : v }))}
              >
                <SelectTrigger
                  data-testid="accessory-location-select"
                  className="bg-slate-950 border-slate-700 rounded-sm font-mono"
                >
                  <SelectValue placeholder="Depo seçin" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-60">
                  <SelectItem value="__none">— Konum seçilmedi —</SelectItem>
                  {uniqueWarehouses.map((w) => (
                    <SelectItem key={w.id} value={w.name}>
                      <span className="font-mono text-blue-400">{w.code}</span>
                      <span className="ml-2">{w.name}</span>
                    </SelectItem>
                  ))}
                  {uniqueWarehouses.length === 0 && (
                    <div className="px-2 py-3 text-center text-xs text-slate-500">
                      Depo tanımlı değil. Ayarlar → Depolar'dan ekleyin.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              {accErrors.length > 0 && (
                <div
                  data-testid="accessory-validation-error"
                  role="alert"
                  className="mr-auto flex items-center gap-2 rounded-sm border border-rose-800 bg-rose-950/60 px-3 py-2 text-xs text-rose-200"
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 text-rose-400" />
                  <span>Zorunlu alan(lar): {accErrors.join(", ")}</span>
                </div>
              )}
              <Button type="button" variant="outline" onClick={() => setAccOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
                İptal
              </Button>
              <Button data-testid="save-accessory-btn" type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingAccessoryId ? "Güncelle" : "Kaydet")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MaterialDetailSheet
        open={detail.open}
        onOpenChange={(v) => setDetail((d) => ({ ...d, open: v }))}
        materialType={detail.type}
        materialId={detail.id}
      />

      <AiStockDraftDialog
        open={aiFabOpen}
        kind="fabrics"
        onClose={() => setAiFabOpen(false)}
        onCreated={() => { setAiFabOpen(false); load(); }}
      />
      <AiStockDraftDialog
        open={aiAccOpen}
        kind="accessories"
        onClose={() => setAiAccOpen(false)}
        onCreated={() => { setAiAccOpen(false); load(); }}
      />
      <QuickSupplierDialog
        open={!!quickSupplierFor}
        onOpenChange={(v) => { if (!v) { setQuickSupplierFor(null); quickSupplierForRef.current = null; } }}
        onCreated={(created) => {
          // useRef üzerinden hedefi güvenle oku (state reset yarışına karşı belt-and-suspenders)
          const target = quickSupplierForRef.current || quickSupplierFor;
          setSuppliers((prev) => [...prev, created]);
          if (target === "accessory") {
            setAccForm((prev) => ({ ...prev, primary_supplier_id: created.id }));
          } else if (target === "fabric") {
            setFabForm((prev) => ({ ...prev, primary_supplier_id: created.id }));
          }
          quickSupplierForRef.current = null;
          setQuickSupplierFor(null);
        }}
      />
      <BulkStockDialog open={bulkStockOpen} onOpenChange={setBulkStockOpen} onDone={() => load()} />
    </div>
  );
}
